import { createFrontendClient } from '@pipedream/sdk/browser';
import config from '../config';
import { send_request } from './Request';

/**
 * Live search of the Pipedream app catalog. Returns the matching apps plus the cursor for the next
 * page. `capability` selects which capability the apps must support ('triggers' today, 'actions' is
 * the forward hook for the future outbound-action feature).
 */
export const searchPipedreamApps = async (
  query = '',
  cursor = null,
  capability = 'triggers'
) => {
  const params = {};
  if (query) params.q = query;
  if (cursor) params.cursor = cursor;
  if (capability) params.capability = capability;

  const response = await send_request(
    'ai-service/pipedream/apps/search',
    '',
    params,
    'GET'
  );
  const data = response?.data;
  return {
    apps: data?.data || [],
    endCursor: data?.pageInfo?.endCursor || null,
    totalCount: data?.pageInfo?.totalCount || 0,
  };
};

/**
 * Apps the user has already connected via Pipedream, mapped to the app-picker shape and de-duplicated
 * by slug (a user may have multiple accounts for the same app). Used to pre-populate the trigger app
 * picker so already-authenticated apps surface without searching.
 */
export const getConnectedPipedreamApps = async () => {
  const response = await send_request(
    'ai-service/credential/user/triggers/display',
    '',
    {},
    'GET'
  );
  const accounts = response?.data || [];
  const bySlug = new Map();
  accounts.forEach(account => {
    const nameSlug = account?.appNameSlug;
    if (nameSlug && !bySlug.has(nameSlug)) {
      bySlug.set(nameSlug, {
        nameSlug,
        name: account?.appName || nameSlug,
        imgSrc: account?.icons?.[0] || '',
      });
    }
  });
  return Array.from(bySlug.values());
};

/**
 * Lists the triggers (events) a Pipedream app exposes. Returns the raw trigger component list
 * ({ name, description, key, configurableProps }) for the event picker.
 */
export const getPipedreamTriggers = async appSlugName => {
  const response = await send_request(
    `ai-service/pipedream/getTriggers/${appSlugName}`,
    '',
    {},
    'GET'
  );
  return response?.data?.data || [];
};

/**
 * Lists the actions a Pipedream app exposes (the action equivalent of getTriggers). Returns the raw
 * action component list ({ name, description, key, version, configurableProps }) for the action picker.
 */
export const getPipedreamActions = async appSlugName => {
  const response = await send_request(
    `ai-service/pipedream/getActions/${appSlugName}`,
    '',
    {},
    'GET'
  );
  return response?.data?.data || [];
};

export const retrieveConnectToken = async clientId => {
  const response = await send_request(
    `ai-service/pipedream/connectToken/${clientId}`,
    '',
    {},
    'GET'
  );
  return response?.data;
};

/**
 * Builds the Pipedream Connect frontend client used by ComponentFormContainer (dynamic-props forms) for
 * both the trigger and action components.
 *
 * The browser SDK omits the project id from connect paths (e.g. getComponent hits
 * `/v1/connect/components/{key}`), but Pipedream's Connect API requires
 * `/v1/connect/{projectId}/components/{key}` and 404s without it. Assigning `projectId` restores the
 * correct, connect-token-authorized path. `externalUserId` is the W86 clientId.
 *
 * `apiHost` points the SDK at our same-origin passthrough (PipedreamConnectProxyController) rather
 * than api.pipedream.com, which does not allow CORS from *.workflow86.com. The SDK builds
 * `https://${apiHost}/v1/connect/...`, and the proxy relays those calls to Pipedream server-side.
 */
export const createPipedreamConnectClient = clientId => {
  const client = createFrontendClient({
    environment: config.PIPEDREAM_ENV,
    externalUserId: clientId,
    tokenCallback: () => retrieveConnectToken(clientId),
    apiHost: config.PIPEDREAM_CONNECT_PROXY_HOST,
  });
  client.projectId = config.PIPEDREAM_PROJECT_ID;
  return client;
};

export const deletePipedreamCredential = async accountId => {
  return await send_request(
    `ai-service/pipedream/credential/delete/${accountId}`,
    '',
    {},
    'DELETE'
  ).then(res => {
    if (!res.status) {
      throw new Error(res);
    }
  });
};
